import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import Logo from '../../../components/Logo/Logo';
import authService from '../../../services/api/authService';
import Toast from '../../../components/Toast/Toast';

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [email, setEmailLocal] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning';
  } | null>(null);

  const isValidEmail = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const isButtonDisabled =
    !email.trim() || !isValidEmail(email) || isChecking;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValidEmail(email)) {
      setToast({
        message: 'Please enter a valid email address',
        type: 'error'
      });
      return;
    }

    setIsChecking(true);

    try {
      await authService.forgotPassword(email);

      setToast({
        message: 'A password reset link has been sent to your email address.',
        type: 'success'
      });

      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (error: any) {
      let errorMessage = 'Something went wrong. Please try again.';

      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setToast({
        message: errorMessage,
        type: 'error'
      });
      setIsChecking(false);
    }
  };
  return (
    <div className="flex min-h-screen">

      {/* ── Left brand panel (desktop only) ── */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 flex-col items-center justify-center bg-gradient-to-br from-brand-primary to-brand-gray p-12 text-white">
        <div className="max-w-xs text-center">
          <div className="mb-3 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20">
            <svg viewBox="0 0 24 24" className="h-8 w-8 fill-white" aria-hidden="true">
              <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8Z" />
              <path d="M12 6a1 1 0 0 0-1 1v5.59l-3.71 3.7a1 1 0 1 0 1.42 1.42l4-4A1 1 0 0 0 13 13V7a1 1 0 0 0-1-1Z" />
            </svg>
          </div>
          <h2 className="mb-3 text-3xl font-bold">Reset Password</h2>
          <p className="text-base leading-relaxed text-white/75">
            We&apos;ll send a secure link to your email so you can reset your password.
          </p>
          <div className="mt-10 flex justify-center gap-2">
            <div className="h-1.5 w-8 rounded-full bg-white/50" />
            <div className="h-1.5 w-2 rounded-full bg-white/30" />
            <div className="h-1.5 w-2 rounded-full bg-white/30" />
          </div>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex flex-1 flex-col items-center justify-center bg-gray-50 px-6 py-12">
        <div className="w-full max-w-sm">

          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <div className="w-36 sm:w-44">
              <Logo />
            </div>
          </div>

          <h1 className="mb-1 text-2xl font-bold text-gray-950">Forgot Password</h1>
          <p className="mb-8 text-sm text-gray-600">
            Enter your email and we&apos;ll send you a link to reset your password.
          </p>


          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Email */}
            <div>
              <label htmlFor="forgot-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Email
              </label>
              <Input
                id="forgot-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmailLocal(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                required
              />
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={isButtonDisabled}
              className="inline-flex w-full items-center justify-center rounded-lg bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {isChecking ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600">
            Remember your password?{' '}
            <button
              type="button"
              onClick={() => navigate('/')}
              className="font-semibold text-brand-primary transition-colors hover:text-brand-hover"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
